import React, { useState, useEffect } from 'react';
import { Button, Box, Typography, Alert, CircularProgress, Paper, TextField, Divider } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

// Get API URL from environment or default to localhost
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

/**
 * Debug component to test authentication state and API access
 */
const AuthDebug: React.FC = () => {
  const { isAuthenticated, user, token, login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Test login form state
  const [email, setEmail] = useState('demo@example.com'); // Pre-filled with demo user
  const [password, setPassword] = useState('demo123'); // Pre-filled with demo password
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  
  // Check token on component mount
  useEffect(() => {
    if (isAuthenticated && token) {
      testTokenAuth();
    }
  }, [isAuthenticated, token]);

  const testTokenAuth = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    
    try {
      const storedToken = localStorage.getItem('token');
      console.log('Testing auth with token:', storedToken ? `${storedToken.substring(0, 10)}...` : 'None');
      
      // Test endpoint that should be protected
      const response = await axios.get(`${API_BASE_URL}/auth/check-token`, {
        headers: {
          'Authorization': `Bearer ${storedToken}`
        },
        withCredentials: true
      });
      
      setResult(response.data);
    } catch (err: any) {
      setError(err.message || 'Unknown error occurred');
      console.error('Auth test error:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError(null);
    
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        email,
        password
      }, { withCredentials: true });
      
      const data = response.data;
      login(
        data.access_token, 
        data.user_role, 
        data.email, 
        data.name, 
        data.publisher_id
      );
      
      setResult({
        message: "Login successful",
        token: data.access_token.substring(0, 10) + '...'
      });
      
      // Check token immediately after login
      setTimeout(testTokenAuth, 500);
    } catch (err: any) {
      setLoginError(err.response?.data?.detail || err.message || 'Login failed');
      console.error('Login error:', err);
    } finally {
      setLoginLoading(false);
    }
  };
  
  const handleDemoLogin = async () => {
    setLoginLoading(true);
    setLoginError(null);
    
    try {
      // First check if API is accessible
      try {
        const healthCheck = await axios.get(`${API_BASE_URL}/health`);
        console.log('API health check:', healthCheck.data);
      } catch (err) {
        console.error('API health check failed:', err);
        setLoginError(`API server unreachable. Make sure the backend is running at ${API_BASE_URL}`);
        setLoginLoading(false);
        return;
      }
      
      // Use demo user credentials
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        email: 'demo@example.com',
        password: 'demo123'
      }, { withCredentials: true });
      
      const data = response.data;
      login(
        data.access_token, 
        data.user_role, 
        data.email, 
        data.name, 
        data.publisher_id
      );
      
      // Store token in localStorage for easier access by the api interceptor
      localStorage.setItem('token', data.access_token);
      
      setResult({
        message: "Demo login successful",
        token: data.access_token.substring(0, 10) + '...',
        user: data.email
      });
      
      // Check token immediately after login
      setTimeout(testTokenAuth, 500);
    } catch (err: any) {
      console.error('Login error:', err);
      
      if (err.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        const errorDetail = err.response.data?.detail || 'Unknown server error';
        const statusCode = err.response.status;
        setLoginError(`Login failed (${statusCode}): ${errorDetail}`);
      } else if (err.request) {
        // The request was made but no response was received
        setLoginError(`No response from server. Please check if the backend is running at ${API_BASE_URL}`);
      } else {
        // Something happened in setting up the request that triggered an Error
        setLoginError(`Error: ${err.message}`);
      }
    } finally {
      setLoginLoading(false);
    }
  };
  
  return (
    <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: 'background.paper' }}>
      <Typography variant="h6">Authentication Status</Typography>
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2">
          Status: <strong>{isAuthenticated ? 'Authenticated' : 'Not Authenticated'}</strong>
        </Typography>
        {user && (
          <>
            <Typography variant="body2">User: {user.email} ({user.user_type})</Typography>
            <Typography variant="body2">Token: {token ? token.substring(0, 15) + '...' : 'None'}</Typography>
          </>
        )}
      </Box>
      
      {!isAuthenticated && (
        <>
          <Alert severity="warning" sx={{ mb: 2 }}>
            You need to be logged in to use this feature. Click the "Quick Demo Login" button below to log in with demo credentials.
          </Alert>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <Button 
              variant="contained" 
              color="primary" 
              size="large"
              disabled={loginLoading}
              onClick={handleDemoLogin}
              sx={{ py: 1, px: 3 }}
            >
              {loginLoading ? <CircularProgress size={24} /> : 'Quick Demo Login'}
            </Button>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle1">Manual Login</Typography>
          <form onSubmit={handleLogin}>
            <TextField
              label="Email"
              variant="outlined"
              fullWidth
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              label="Password"
              type="password"
              variant="outlined"
              fullWidth
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button type="submit" variant="outlined" disabled={loginLoading}>
                {loginLoading ? <CircularProgress size={24} /> : 'Login'}
              </Button>
            </Box>
          </form>
        </>
      )}
      
      {isAuthenticated && (
        <Button variant="outlined" onClick={testTokenAuth} disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Test Auth Token'}
        </Button>
      )}
      
      {loginError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {loginError}
        </Alert>
      )}
      
      {result && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1">Result:</Typography>
          <pre style={{ background: '#f5f5f5', padding: 10, overflow: 'auto' }}>
            {JSON.stringify(result, null, 2)}
          </pre>
        </Box>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Paper>
  );
};

export default AuthDebug; 